import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqData = [
    {
      id: 1,
      category: "Products",
      question: "What makes Mava bottles different from other brands?",
      answer: `Mava bottles combine premium materials with innovative technology. Our Steel Heritage collection features food-grade stainless steel with advanced insulation that maintains temperature for up to 24 hours. The Smart Innovation series includes IoT sensors for hydration tracking, while our Limited Editions showcase exclusive designs with sustainable materials like bamboo fiber composites.`
    },
    {
      id: 2,
      category: "Ordering",
      question: "How do I place a bulk order for my company?",
      answer: `For corporate orders, simply fill out our enquiry form and select 'Business/Corporate' as your use case. Our B2B team will contact you within 24 hours to discuss volume discounts, customization options, and delivery timelines. We offer special pricing for orders above 50 units and provide custom branding services.`
    },
    {
      id: 3,
      category: "Customization",
      question: "Can I get my company logo engraved on the bottles?",
      answer: `Yes! We offer laser engraving, UV printing, and embossing services for corporate clients. Minimum order quantities apply based on the customization method. Laser engraving starts from 25 units, while UV printing requires a minimum of 100 units. Setup costs vary by complexity and method chosen.`
    },
    {
      id: 4,
      category: "Shipping",
      question: "What are your delivery timelines and charges?",
      answer: `Standard delivery takes 3-5 business days across India with free shipping on orders above ₹2,000. Express delivery (1-2 days) is available in major cities for an additional ₹200. Bulk orders may require 7-14 days depending on customization requirements. International shipping is available to select countries.`
    },
    {
      id: 5,
      category: "Warranty",
      question: "What warranty do you provide on Mava bottles?",
      answer: `All Mava bottles come with a 2-year manufacturer warranty covering defects in materials and workmanship. Smart bottles include an additional 1-year warranty on electronic components. The warranty covers insulation performance, leak-proof guarantee, and structural integrity. Normal wear and tear is not covered.`
    },
    {
      id: 6,
      category: "Care",
      question: "How should I clean and maintain my Mava bottle?",
      answer: `For daily cleaning, rinse with warm water and mild soap. For deep cleaning, use our recommended cleaning tablets or a mixture of baking soda and warm water. Avoid abrasive cleaners or bleach. Smart bottles should not be submerged completely - clean the sensor area with a damp cloth only. Detailed care instructions are included with each purchase.`
    },
    {
      id: 7,
      category: "Technology",
      question: "How does the smart bottle technology work?",
      answer: `Our Smart Innovation bottles feature built-in sensors that track water temperature, consumption patterns, and hydration goals. The companion mobile app syncs via Bluetooth to provide personalized hydration reminders, health insights, and usage analytics. The battery lasts up to 6 months and is rechargeable via USB-C.`
    },
    {
      id: 8,
      category: "Sustainability",
      question: "Are Mava bottles environmentally friendly?",
      answer: `Absolutely! Our bottles are designed to replace thousands of single-use plastic bottles. We use recycled materials where possible, carbon-neutral shipping, and partner with environmental organizations. Each bottle purchase contributes to our ocean cleanup initiative. Our packaging is 100% recyclable and plastic-free.`
    }
  ];

  const categories = [...new Set(faqData.map(faq => faq.category))];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="bg-card rounded-2xl liquid-shadow p-8"
    >
      <div className="text-center mb-8">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
            <Icon name="HelpCircle" size={24} color="white" />
          </div>
          <div>
            <h3 className="text-2xl font-poppins font-bold text-text-primary">
              Frequently Asked Questions
            </h3>
          </div>
        </div>
        <p className="text-text-secondary">
          Find quick answers to common questions about Mava products and services
        </p>
      </div>
      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {categories?.map((category) => (
          <span
            key={category}
            className="px-4 py-2 bg-surface text-text-secondary rounded-full text-sm font-medium"
          >
            {category}
          </span>
        ))}
      </div>
      {/* FAQ Items */}
      <div className="space-y-4">
        {faqData?.map((faq) => (
          <motion.div
            key={faq?.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="border border-border rounded-xl overflow-hidden"
          >
            <motion.button
              onClick={() => toggleFAQ(faq?.id)}
              className="w-full p-6 text-left hover:bg-muted brand-transition focus:outline-none focus:ring-2 focus:ring-primary/20"
              whileHover={{ backgroundColor: 'var(--color-muted)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full flex-shrink-0 mt-1">
                    {faq?.category}
                  </span>
                  <h4 className="font-poppins font-semibold text-text-primary text-left">
                    {faq?.question}
                  </h4>
                </div>
                <motion.div
                  animate={{ rotate: openFAQ === faq?.id ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-shrink-0 ml-4"
                >
                  <Icon 
                    name="ChevronDown" 
                    size={20} 
                    className={openFAQ === faq?.id ? 'text-primary' : 'text-text-secondary'} 
                  />
                </motion.div>
              </div>
            </motion.button>

            <AnimatePresence>
              {openFAQ === faq?.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 pt-2">
                    <div className="pl-20">
                      <p className="text-text-secondary leading-relaxed">
                        {faq?.answer}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
      {/* Contact Support */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-8 p-6 bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 rounded-xl text-center"
      >
        <div className="flex items-center justify-center space-x-2 mb-3">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          <h4 className="font-poppins font-semibold text-text-primary">
            Still have questions?
          </h4>
        </div>
        <p className="text-text-secondary mb-4">
          Our customer support team is here to help you with any additional queries.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 brand-transition"
            onClick={() => window.open('mailto:support@mava.in', '_self')}
          >
            <Icon name="Mail" size={16} />
            <span>Email Support</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 brand-transition"
            onClick={() => window.open('https://wa.me/919876543210', '_blank')}
          >
            <Icon name="MessageCircle" size={16} />
            <span>WhatsApp Chat</span>
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FAQSection;